import { useEffect, useRef, useState } from "react";
import { Upload, X, Loader2, Check } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const SLOTS = [
    {
        section: "Proof of Identity — Driver License",
        sub: "Used to verify your identity and ensure accuracy.",
        slots: [
            { id: "license_front", label: "License — front" },
            { id: "license_back", label: "License — back" },
        ],
    },
    {
        section: "Proof of Social Security",
        sub: "SSC or tax document.",
        slots: [{ id: "ssn_document", label: "SSN document", full: true }],
    },
    {
        section: "Proof of Address",
        sub: "Accepted: electric, water, gas, phone, bank, credit card. Must match address exactly. Within 30–60 days.",
        slots: [
            { id: "primary_bill", label: "Primary bill" },
            { id: "secondary_bill", label: "Secondary bill (optional)" },
        ],
    },
    {
        section: "Additional Documents (optional)",
        sub: "Upload any other supporting documents. No restrictions.",
        slots: [
            { id: "extra_1", label: "Extra document 1", third: true },
            { id: "extra_2", label: "Extra document 2", third: true },
            { id: "extra_3", label: "Extra document 3", third: true },
        ],
    },
];

function findFileForSlot(files, slotId) {
    return (files || []).find((f) => f.slot === slotId && !f.is_deleted);
}

function FilePreview({ file, onLoadPreview }) {
    const [src, setSrc] = useState(null);
    useEffect(() => {
        let revoke = null;
        const isImage = (file.content_type || "").startsWith("image/");
        if (isImage && file.localBlobUrl) {
            setSrc(file.localBlobUrl);
        } else if (isImage && onLoadPreview) {
            onLoadPreview(file).then((url) => {
                setSrc(url);
                revoke = url;
            });
        }
        return () => {
            if (revoke) URL.revokeObjectURL(revoke);
        };
    }, [file, onLoadPreview]);

    if ((file.content_type || "").startsWith("image/")) {
        return (
            <div className="mt-3 rounded-lg bg-[#0a1628]/60 border border-[#D4AF37]/15 py-4 flex flex-col items-center">
                {src ? (
                    <img src={src} alt={file.original_filename} className="max-h-32 rounded-md object-contain" />
                ) : (
                    <div className="h-24 w-24 rounded-md bg-white/5 animate-pulse" />
                )}
                <div className="mt-2 text-xs text-[#D4AF37] inline-flex items-center gap-1">
                    <Check size={12} /> Upload successful
                </div>
            </div>
        );
    }
    return (
        <div className="mt-3 rounded-lg bg-[#0a1628]/60 border border-[#D4AF37]/15 py-4 px-4 flex items-center justify-center text-xs text-[#D4AF37] gap-1">
            <Check size={12} /> Upload successful
        </div>
    );
}

function UploadSlot({ slot, file, busy, onPick, onRemove, onLoadPreview }) {
    const filled = !!file;
    return (
        <div className="space-y-2">
            <div className="field-label">{slot.label}</div>
            <div
                className={`rounded-xl border ${
                    filled
                        ? "border-[#D4AF37]/35 bg-[#0F1E36]/60"
                        : "border-dashed border-[#D4AF37]/25 bg-[#0F1E36]/40 hover:border-[#D4AF37]/55 hover:bg-[#D4AF37]/[0.04]"
                } transition-all`}
            >
                {filled ? (
                    <div className="p-3">
                        <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-3 min-w-0">
                                <div className="w-9 h-9 rounded-md bg-[#D4AF37]/12 border border-[#D4AF37]/35 flex items-center justify-center shrink-0">
                                    <Check size={16} className="text-[#D4AF37]" />
                                </div>
                                <div className="min-w-0">
                                    <div className="text-sm text-white truncate" data-testid={`slot-filename-${slot.id}`}>
                                        {file.original_filename}
                                    </div>
                                    <div className="text-xs text-white/45">{(file.size / 1024).toFixed(0)} KB</div>
                                </div>
                            </div>
                            <button
                                onClick={() => onRemove(file.file_id)}
                                className="text-white/45 hover:text-red-400 transition p-1"
                                aria-label="Remove"
                                data-testid={`slot-remove-${slot.id}`}
                            >
                                <X size={16} />
                            </button>
                        </div>
                        <FilePreview file={file} onLoadPreview={onLoadPreview} />
                    </div>
                ) : (
                    <button
                        onClick={() => onPick(slot.id)}
                        disabled={busy}
                        className="w-full text-left px-5 py-6 flex items-center gap-4 disabled:opacity-60 disabled:cursor-wait"
                        data-testid={`slot-upload-${slot.id}`}
                    >
                        <div className="w-10 h-10 rounded-md bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center shrink-0">
                            {busy ? (
                                <Loader2 size={16} className="animate-spin text-[#D4AF37]" />
                            ) : (
                                <Upload size={16} className="text-[#D4AF37]" />
                            )}
                        </div>
                        <div className="min-w-0">
                            <div className="text-sm text-white font-medium">Tap to upload</div>
                            <div className="text-xs text-white/55">Image or PDF</div>
                        </div>
                    </button>
                )}
            </div>
        </div>
    );
}

export default function StepDocuments({ token, files, onFilesChange }) {
    const inputRef = useRef(null);
    const [pendingSlot, setPendingSlot] = useState(null);
    const [busy, setBusy] = useState(null);

    const onPick = (slotId) => {
        setPendingSlot(slotId);
        requestAnimationFrame(() => inputRef.current?.click());
    };

    const onFileChosen = async (e) => {
        const file = e.target.files?.[0];
        if (!file || !pendingSlot) return;
        const slotId = pendingSlot;
        setBusy(slotId);
        const localBlobUrl = file.type.startsWith("image/") ? URL.createObjectURL(file) : null;
        try {
            const existing = findFileForSlot(files, slotId);
            if (existing) await api.delete(`/intake/upload/${token}/${existing.file_id}`);
            const form = new FormData();
            form.append("file", file);
            const { data } = await api.post(
                `/intake/upload/${token}?slot=${encodeURIComponent(slotId)}`,
                form,
                { headers: { "Content-Type": "multipart/form-data" } },
            );
            const next = (files || []).filter((f) => f.slot !== slotId);
            next.push({ ...data, slot: slotId, localBlobUrl });
            onFilesChange(next);
        } catch (err) {
            if (localBlobUrl) URL.revokeObjectURL(localBlobUrl);
            toast.error(err?.response?.data?.detail || `Could not upload ${file.name}`);
        } finally {
            setBusy(null);
            setPendingSlot(null);
            if (inputRef.current) inputRef.current.value = "";
        }
    };

    const remove = async (fileId) => {
        try {
            await api.delete(`/intake/upload/${token}/${fileId}`);
            onFilesChange((files || []).filter((f) => f.file_id !== fileId));
        } catch {
            toast.error("Could not remove file");
        }
    };

    return (
        <div className="space-y-8" data-testid="step-documents">
            <div>
                <div className="step-eyebrow">STEP 3</div>
                <h2 className="font-display text-3xl sm:text-4xl text-white mt-2">Document Uploads</h2>
                <p className="mt-2 text-sm text-white/65">Must be clear, not cropped, not blurry.</p>
            </div>

            {SLOTS.map((section) => (
                <section key={section.section} className="space-y-4">
                    <div>
                        <h3 className="font-sans text-base font-semibold text-white">{section.section}</h3>
                        <p className="text-sm text-white/55 mt-1">{section.sub}</p>
                    </div>
                    <div
                        className={`grid gap-4 ${
                            section.slots.some((s) => s.third)
                                ? "grid-cols-1 sm:grid-cols-3"
                                : section.slots.some((s) => s.full)
                                ? "grid-cols-1"
                                : "grid-cols-1 sm:grid-cols-2"
                        }`}
                    >
                        {section.slots.map((slot) => (
                            <UploadSlot
                                key={slot.id}
                                slot={slot}
                                file={findFileForSlot(files, slot.id)}
                                busy={busy === slot.id}
                                onPick={onPick}
                                onRemove={remove}
                            />
                        ))}
                    </div>
                </section>
            ))}

            <input
                ref={inputRef}
                type="file"
                accept=".pdf,image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={onFileChosen}
                data-testid="upload-input"
            />
        </div>
    );
}
